import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, Subscription, catchError, map, of, tap } from 'rxjs';
import { environment } from '../../environments/environment';

/** Collapses the triggers of a single navigation; every later navigation re-counts. */
const NAVIGATION_REFRESH_MIN_MS = 2_000;

/**
 * Unread-application badge next to «Inserate & Bewerbungen» in the sidebar.
 *
 * "Unread" is backend state (`job_applications.viewed_at`): a PENDING application
 * no school admin has opened yet. It is shared across the team and devices, so
 * one colleague opening an application clears it for everyone.
 */
@Injectable({ providedIn: 'root' })
export class ApplicationUnreadService {
  private readonly http = inject(HttpClient);
  private readonly apiBase = environment.apiBaseUrl;

  private readonly _count = signal(0);
  readonly count = this._count.asReadonly();

  private scopeId: string | null = null;
  private inFlight?: Subscription;
  private lastRequestedAt = 0;

  /**
   * Re-count unread applications for an HR scope: a Schulgemeinde authority id
   * or a Schuleinheit root id — the same id the «Inserate & Bewerbungen» link
   * opens. `GET /v1/job-postings/schools/{id}` (for_profile) already aggregates
   * that scope (authority → every unit + Standort; unit → its Standorte), so one
   * request covers exactly the postings the HR page lists.
   */
  refresh(scopeId: string | null): void {
    if (scopeId !== this.scopeId) {
      this._count.set(0);
    }
    this.scopeId = scopeId;
    this.inFlight?.unsubscribe();
    if (!scopeId) {
      return;
    }
    this.lastRequestedAt = Date.now();
    this.inFlight = this.http
      .get<{ count_unread_applications?: number }[]>(
        `${this.apiBase}/v1/job-postings/schools/${scopeId}`,
        { params: { is_archived: false } }
      )
      .pipe(
        map(postings =>
          postings.reduce(
            (sum, p) => sum + (p.count_unread_applications ?? 0),
            0
          )
        ),
        catchError(err => {
          console.log('[unread-badge] request failed', { scopeId, err });
          return of(null);
        })
      )
      .subscribe(count => {
        // TODO(unread-badge): temporary debug logging, remove once the badge works.
        console.log('[unread-badge] count', { scopeId, count });
        if (count !== null) {
          this._count.set(count);
        }
      });
  }

  /**
   * Like {@link refresh}, but skips the request when the same scope was
   * requested within {@link NAVIGATION_REFRESH_MIN_MS}. Every navigation
   * triggers this, and the request lists every posting of the scope.
   */
  refreshIfStale(scopeId: string | null): void {
    if (
      scopeId !== null &&
      scopeId === this.scopeId &&
      Date.now() - this.lastRequestedAt < NAVIGATION_REFRESH_MIN_MS
    ) {
      return;
    }
    this.refresh(scopeId);
  }

  /** PUT …/viewed (idempotent), then re-count so the sidebar badge drops right away. */
  markViewed(jobPostingId: string, applicationId: string): Observable<void> {
    return this.http
      .put<unknown>(
        `${this.apiBase}/v1/job-postings/${jobPostingId}/applications/${applicationId}/viewed`,
        {}
      )
      .pipe(
        tap(() => this.refresh(this.scopeId)),
        map(() => undefined)
      );
  }
}
